/**
 * Operator Management Section
 * @description Portal 운영자 관리 - CRUD 기능
 */

import { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@shared/ui/primitives';
import { Plus, Pencil, Trash2, Key } from 'lucide-react';
import {
  getOperators,
  createOperator,
  updateOperator,
  deleteOperator,
  type Operator,
} from '@/services/operatorApi';
import { PasskeyRegistrationDialog } from '@/components/passkey/PasskeyRegistrationDialog';
import { PasskeyList } from '@/components/passkey/PasskeyList';
import { useAuthStore } from '@/stores/authStore';
import { useLocale, usePortalTranslation } from '@shared/i18n';

export const OperatorPage: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = usePortalTranslation('admin');
  const { locale } = useLocale();
  const dateLocale = locale === 'en' ? 'en-US' : 'ko-KR';
  const [operators, setOperators] = useState<Operator[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<Operator | null>(null);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  
  // Passkey 관리 상태
  const [isPasskeyDialogOpen, setIsPasskeyDialogOpen] = useState(false);
  const [passkeyAdminUserId, setPasskeyAdminUserId] = useState('');
  const [passkeyRefreshTrigger, setPasskeyRefreshTrigger] = useState(0);

  // Load operators only when authenticated as portal-admin (avoids 401 + alert after logout)
  useEffect(() => {
    if (user?.userType !== 'portal-admin') return;
    loadOperators();
  }, [user?.userType]);

  const loadOperators = async () => {
    try {
      setIsLoading(true);
      const data = await getOperators();
      setOperators(data);
    } catch (error) {
      console.error('Error loading operators:', error);
      const msg = error instanceof Error ? error.message : '';
      // 401 시 이미 로그인 페이지로 리다이렉트 중이므로 알림 생략
      if (msg.includes('인증이 만료') || msg.includes('다시 로그인')) return;
      alert(t('operator.alertLoadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingOperator(null);
    setFormData({ username: '', password: '' });
    setIsDialogOpen(true);
  };

  const handleEdit = (operator: Operator) => {
    setEditingOperator(operator);
    setFormData({ username: operator.username, password: '' });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('operator.confirmDelete'))) {
      return;
    }

    try {
      setIsLoading(true);
      await deleteOperator(id);
      await loadOperators(); // 목록 다시 불러오기
    } catch (error: any) {
      console.error('Error deleting operator:', error);
      const msg = error?.message ?? '';
      if (msg.includes('인증이 만료') || msg.includes('다시 로그인')) return;
      alert(error.response?.message || error.message || t('operator.alertDeleteError'));
    } finally {
      setIsLoading(false);
    }
  };

  /** 운영자 비밀번호 정책 검사 (8자 이상, 대·소문자·숫자·특수문자) */
  const validateOperatorPassword = (password: string): string | null => {
    if (password.length < 8) return t('operator.passwordMinLength');
    if (!/[A-Z]/.test(password)) return t('operator.passwordUpper');
    if (!/[a-z]/.test(password)) return t('operator.passwordLower');
    if (!/\d/.test(password)) return t('operator.passwordDigit');
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return t('operator.passwordSpecial');
    return null;
  };

  const handleSave = async () => {
    if (!formData.username.trim()) {
      alert(t('operator.alertUsernameRequired'));
      return;
    }

    const passwordToSet = formData.password.trim();
    if (editingOperator) {
      if (passwordToSet) {
        const err = validateOperatorPassword(passwordToSet);
        if (err) {
          alert(err);
          return;
        }
      }
    } else {
      if (!passwordToSet) {
        alert(t('operator.alertPasswordRequired'));
        return;
      }
      const err = validateOperatorPassword(passwordToSet);
      if (err) {
        alert(err);
        return;
      }
    }

    setIsLoading(true);
    try {
      if (editingOperator) {
        const updateData: { username?: string; password?: string } = {
          username: formData.username.trim(),
        };
        if (passwordToSet) updateData.password = passwordToSet;
        await updateOperator(editingOperator.id, updateData);
      } else {
        await createOperator({
          username: formData.username.trim(),
          password: passwordToSet,
        });
      }
      
      setIsDialogOpen(false);
      setFormData({ username: '', password: '' });
      setEditingOperator(null);
      await loadOperators(); // 목록 다시 불러오기
    } catch (error: any) {
      console.error('Error saving operator:', error);
      const msg = error?.message ?? '';
      if (msg.includes('인증이 만료') || msg.includes('다시 로그인')) return;
      const errorMessage = error.response?.message || error.message || t('operator.alertSaveError');
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyRegister = (adminUserId?: string) => {
    setPasskeyAdminUserId(adminUserId || '');
    setIsPasskeyDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('operator.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('operator.subtitle')}
          </p>
        </div>
      </div>

      <Tabs defaultValue="operators" className="w-full">
        <TabsList>
          <TabsTrigger value="operators">{t('operator.tabOperators')}</TabsTrigger>
          <TabsTrigger value="passkeys">{t('operator.tabPasskeys')}</TabsTrigger>
        </TabsList>

        <TabsContent value="operators" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('operator.listTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('operator.listSubtitle')}
              </p>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-2" />
              {t('operator.addOperator')}
            </Button>
          </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('operator.colUsername')}</TableHead>
              <TableHead>{t('operator.colCreatedAt')}</TableHead>
              <TableHead>{t('operator.colUpdatedAt')}</TableHead>
              <TableHead className="text-right">{t('operator.colActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  {t('operator.emptyOperators')}
                </TableCell>
              </TableRow>
            ) : (
              operators.map((operator) => (
                <TableRow key={operator.id}>
                  <TableCell className="font-medium">{operator.username}</TableCell>
                  <TableCell>
                    {operator.createdAt ? new Date(operator.createdAt).toLocaleString(dateLocale) : '-'}
                  </TableCell>
                  <TableCell>
                    {operator.updatedAt ? new Date(operator.updatedAt).toLocaleString(dateLocale) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(operator)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(operator.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingOperator ? t('operator.dialogEditTitle') : t('operator.dialogAddTitle')}
            </DialogTitle>
            <DialogDescription>
              {editingOperator
                ? t('operator.dialogEditDesc')
                : t('operator.dialogAddDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="username" className="text-sm font-medium">
                {t('operator.labelUsername')} <span className="text-red-500">*</span>
              </label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder={t('operator.placeholderUsername')}
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="password" className="text-sm font-medium">
                {t('operator.labelPassword')} {editingOperator ? '' : <span className="text-red-500">*</span>}
              </label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingOperator ? t('operator.placeholderPasswordOptional') : t('operator.placeholderPasswordNew')}
              />
              <p className="text-xs text-muted-foreground">
                {t('operator.passwordHint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t('operator.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? t('operator.saving') : t('operator.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="passkeys" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('operator.passkeyTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('operator.passkeySubtitle')}
              </p>
            </div>
            <Button onClick={() => handlePasskeyRegister()}>
              <Key className="w-4 h-4 mr-2" />
              {t('operator.registerPasskey')}
            </Button>
          </div>

          <PasskeyList 
            onRefresh={() => setPasskeyRefreshTrigger((prev) => prev + 1)} 
            refreshTrigger={passkeyRefreshTrigger}
            adminUserId={user?.userType === 'portal-admin' ? user.adminUserId : undefined}
          />
        </TabsContent>
      </Tabs>

      {/* Passkey 등록 다이얼로그 */}
      <PasskeyRegistrationDialog
        open={isPasskeyDialogOpen}
        onOpenChange={setIsPasskeyDialogOpen}
        adminUserId={passkeyAdminUserId}
        operators={operators}
        onSuccess={() => {
          setPasskeyRefreshTrigger((prev) => prev + 1);
        }}
      />
    </div>
  );
};

OperatorPage.displayName = 'OperatorPage';

